/* ── Hero height — exact window height, updates on resize ── */
const hero = document.getElementById('hero');
function setHeroHeight() {
  if (!hero) return;
  hero.style.setProperty('--hero-h', window.innerHeight + 'px');
}
setHeroHeight();
window.addEventListener('resize', setHeroHeight, { passive: true });

/* ── Reduced-motion gate (used by ambient loops) ── */
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ── Scroll reveal: fade + rise once per element ── */
const revealIO = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (!entry.isIntersecting) return;
    entry.target.classList.add('revealed');
    entry.target.style.willChange = 'auto';
    revealIO.unobserve(entry.target);
  });
}, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

document.querySelectorAll('.reveal, .reveal-stagger').forEach((el) => {
  el.style.willChange = 'opacity, transform';
  revealIO.observe(el);
});

window.revealIO = revealIO;

/* ── Hero video lazy-load: the poster paints LCP, the actual video
   bytes don't start downloading until the browser is idle. Without
   this, autoplay forces the video into the critical-path queue and
   competes with fonts, the 3D bundle, and product imagery. */
const heroVideo = document.getElementById('hero-video');
if (heroVideo) {
  const lazySource = heroVideo.querySelector('source[data-src]');
  let started = false;
  function loadHeroVideo() {
    if (started || !lazySource) return;
    started = true;
    lazySource.src = lazySource.dataset.src;
    heroVideo.load();
    if (!prefersReducedMotion) heroVideo.play().catch(() => {});
  }
  if ('requestIdleCallback' in window) {
    requestIdleCallback(loadHeroVideo, { timeout: 1500 });
  } else {
    window.addEventListener('load', () => setTimeout(loadHeroVideo, 200), { once: true });
  }
}

/* ── Hero video parallax — slow vertical drift on scroll ── */
if (heroVideo && !prefersReducedMotion) {
  heroVideo.classList.add('parallax');
  let heroTicking = false;
  function updateHeroParallax() {
    const y = Math.min(window.scrollY, window.innerHeight);
    heroVideo.style.transform = `translate3d(0, ${y * 0.1}px, 0) scale(1.15)`;
    heroTicking = false;
  }
  window.addEventListener('scroll', () => {
    if (heroTicking) return;
    heroTicking = true;
    requestAnimationFrame(updateHeroParallax);
  }, { passive: true });
  updateHeroParallax();
}

/* ── Stat count-up: parse numeric stats, animate 0 → target on reveal ── */
function countUp(el, target, suffix, prefix, duration = 1400) {
  const start = performance.now();
  function step(now) {
    const t = Math.min((now - start) / duration, 1);
    // ease-out cubic
    const eased = 1 - Math.pow(1 - t, 3);
    const value = Math.round(target * eased);
    el.textContent = `${prefix}${value}${suffix}`;
    if (t < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}

document.querySelectorAll('.stat-num').forEach((el) => {
  const raw = el.textContent.trim();
  const match = raw.match(/^(\D*)(\d+)(\D*)$/);
  if (!match) return;
  const [, prefix, digits, suffix] = match;
  const target = parseInt(digits, 10);
  if (prefersReducedMotion) return;
  el.textContent = `${prefix}0${suffix}`;
  const statIO = new IntersectionObserver(([entry]) => {
    if (!entry.isIntersecting) return;
    countUp(el, target, suffix, prefix);
    statIO.unobserve(el);
  }, { threshold: 0.6 });
  statIO.observe(el);
});

/* ── Header: fixed at top until hero bottom reaches nav, then docks ── */
const siteHeader = document.getElementById('site-header');
if (siteHeader && hero) {
  function updateHeaderDock() {
    const heroBottom = hero.getBoundingClientRect().bottom;
    const headerH    = siteHeader.offsetHeight;
    siteHeader.classList.toggle('docked', heroBottom <= headerH);
  }
  window.addEventListener('scroll', updateHeaderDock, { passive: true });
  window.addEventListener('resize', updateHeaderDock, { passive: true });
  updateHeaderDock();
}

/* ── Story image: transition to colour when scrolled into view ── */
const storyVisual = document.querySelector('.story-visual');
if (storyVisual) {
  new IntersectionObserver(([entry]) => {
    storyVisual.classList.toggle('in-view', entry.isIntersecting);
  }, { threshold: 0.3 }).observe(storyVisual);
}

/* ── Product cards: colour on scroll (mobile only, effect scoped to ≤560px CSS) ── */
document.querySelectorAll('.prod-card').forEach(card => {
  let cardTimer;
  new IntersectionObserver(([entry]) => {
    clearTimeout(cardTimer);
    cardTimer = setTimeout(() => {
      card.classList.toggle('in-view', entry.isIntersecting);
    }, 1000);
  }, { threshold: 0.3 }).observe(card);
});

/* ── Blog cards + featured post: greyscale → colour as they enter the viewport ── */
const blogColorIO = new IntersectionObserver((entries) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      entry.target.classList.add('in-view');
      blogColorIO.unobserve(entry.target);
    }
  });
}, { threshold: 0.25, rootMargin: '0px 0px -8% 0px' });

document.querySelectorAll('.post-card, .featured-post-section').forEach(el => {
  blogColorIO.observe(el);
});

/* ── Modal open / close ── */
const modal      = document.getElementById('video-modal');
const modalVideo = document.getElementById('modal-video');
const closeBtn   = document.getElementById('modal-close');
const playBtn    = document.getElementById('play-btn');

if (modal && modalVideo && closeBtn && playBtn) {
  function openModal() {
    modalVideo.currentTime = 0;
    modal.style.display = 'flex';
    modal.getBoundingClientRect();
    modal.classList.add('active');
    document.body.classList.add('modal-open');
    modalVideo.play().catch(() => {});
  }

  function closeModal() {
    modal.classList.remove('active');
    document.body.classList.remove('modal-open');
    modalVideo.pause();
    playBtn.style.transform = '';
    setTimeout(() => { modal.style.display = 'none'; }, 300);
  }

  playBtn.addEventListener('mouseenter', () => hero && hero.classList.add('video-hover'));
  playBtn.addEventListener('mouseleave', () => hero && hero.classList.remove('video-hover'));
  playBtn.addEventListener('click', openModal);
  playBtn.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') openModal(); });

  closeBtn.addEventListener('click', closeModal);
  modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('active')) closeModal();
  });
}

/* ── Description toggle (tablet/mobile only) ── */
const descToggle = document.getElementById('hero-desc-toggle');
const heroDesc   = document.getElementById('hero-desc');
if (descToggle && heroDesc) {
  descToggle.addEventListener('click', () => {
    const isOpen = heroDesc.classList.toggle('open');
    descToggle.setAttribute('aria-expanded', isOpen);
    descToggle.querySelector('.hero-desc-toggle-icon').textContent = isOpen ? '×' : '+';
    descToggle.querySelector('.hero-desc-toggle-label').textContent = isOpen ? 'Close' : 'View Description';
  });
}

/* ── Add-to-cart: submit via fetch, update header count, no redirect ── */
function setCartCount(count) {
  document.querySelectorAll('.nav-cart, .nav-drawer-cart').forEach(el => {
    el.textContent = 'Cart (' + count + ')';
  });
}

document.querySelectorAll('.prod-atc-form').forEach(form => {
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = form.querySelector('.add-to-cart');
    if (!btn || btn.disabled) return;

    const originalLabel = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    try {
      const addUrl  = (window.routes && window.routes.cart_add_url) || '/cart/add';
      const cartUrl = (window.routes && window.routes.cart_url)     || '/cart';
      const addRes = await fetch(addUrl + '.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: new FormData(form)
      });
      if (!addRes.ok) throw new Error('add failed');
      const cartRes = await fetch(cartUrl + '.js', { headers: { 'Accept': 'application/json' } });
      const cart = await cartRes.json();
      setCartCount(cart.item_count);
      window.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      btn.textContent = 'Added ✓';
      setTimeout(() => { btn.textContent = originalLabel; btn.disabled = false; }, 1200);
    } catch (err) {
      btn.textContent = originalLabel;
      btn.disabled = false;
      // Fallback to native form post so the user isn't stranded
      form.submit();
    }
  });
});

/* ── Ingredient video: hover-to-play (desktop) / scroll-center-to-play (mobile) ── */
(function () {
  const tiles = document.querySelectorAll('.ing-tile');
  if (!tiles.length) return;

  const isHoverDevice = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  tiles.forEach((tile) => {
    const video = tile.querySelector('.ing-video');
    if (!video) return;

    if (isHoverDevice) {
      tile.addEventListener('mouseenter', () => { video.play().catch(() => {}); });
      tile.addEventListener('mouseleave', () => { video.pause(); video.currentTime = 0; });
    } else {
      const mobileIO = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
            video.currentTime = 0;
          }
        });
      }, { threshold: 0.5 });
      mobileIO.observe(tile);
    }
  });
}());

/* ── Hamburger menu toggle ── */
const hamburger = document.getElementById('nav-hamburger');
const navDrawer = document.getElementById('nav-drawer');
if (hamburger && navDrawer) {
  hamburger.addEventListener('click', () => {
    const isOpen = hamburger.classList.toggle('open');
    navDrawer.classList.toggle('open', isOpen);
    hamburger.setAttribute('aria-expanded', isOpen);
  });
  navDrawer.querySelectorAll('a').forEach(link => {
    link.addEventListener('click', () => {
      hamburger.classList.remove('open');
      navDrawer.classList.remove('open');
      hamburger.setAttribute('aria-expanded', 'false');
    });
  });
}
